/* eslint-disable consistent-return */
/* eslint-disable react/no-array-index-key */
import { Button, Col, Row } from 'antd';
import arrayMove from 'array-move';
import { INPUT_TYPE, LABEL_TYPE } from 'components/common/Input/types';
import { Form, Formik } from 'formik';
import useFormikRefHook from 'hooks/useFormikRefHook';
import useWindowSize from 'hooks/useWindowSize';
import { cloneDeep, get, isEmpty, isObject } from 'lodash';
import React, { ReactElement, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { SortEnd } from 'react-sortable-hoc';
import { ContactNumberType } from 'types/user';
import IntlMessages from 'utils/IntlMessages';
import * as Yup from 'yup';

import PortalProfileSaveButton from '../PortalProfileSaveButton';
import {
  AddMoreButtonStyled,
  FormActionButtonStyled,
  InputComponentStyled,
  SideFieldContainerColStyled,
} from '../styled';
import { IUpdateProfileTemplateProps } from '../types';
import {
  getAddMoreButtonOnClickFunction,
  getBaseInputName,
  getClearButtonOnClickFunction,
  useUpdateUser,
} from './helpers';
import {
  ClearButtonIconContainerStyled,
  ErrorRequiredStyled,
  LabelsContainerStyled,
  SectionHeaderContainerStyled,
  SectionTitleContainerStyled,
} from './styled';
import { INewInitalFieldsProps } from './types';
import UpdateProfileInput from './UpdateProfileInput';
import UpdateProfileInputsContainer from './UpdateProfileInputsContainer';

const WithAddableInputFormTemplate: React.FC<IUpdateProfileTemplateProps<INewInitalFieldsProps>> = ({
  userData,
  initialFields,
  validation,
  onSubmit,
  onSubmitCallback,
  name,
  loading,
  displaySaveOnFooter,
  hideSaveButton,
  submitButtonLabel,
  portalButtonClassName,
  showNotificationOnSuccess = true,
  showNotificationOnFailure = true,
  inputComponentsFromOutside,
  formRef,
  className,
}) => {
  const location = useLocation();
  const { pathname } = location;
  const [sectionInputFields, setSectionInputFields] = useState<INewInitalFieldsProps[]>(cloneDeep(initialFields));
  const { width } = useWindowSize();
  // Set validation object due to having ability to add more fields
  const [validationState, setValidationState] = useState<Record<string, any>>(validation);
  const ref = useFormikRefHook(name);
  // inputErrorRef is error for input that has addable property but hasn't been added any.
  const inputErrorRef = useRef<boolean>(false);

  const isContactTypeField = (field: { type?: string; inputOptions?: { label: string; value: string }[] }) =>
    field?.type === INPUT_TYPE.SELECT &&
    (field?.inputOptions?.some((o) => o.value === ContactNumberType.PHONE) ||
      field?.inputOptions?.some((o) => o.value === ContactNumberType.CELL));

  const handleContactTypeChange = (type: ContactNumberType, sideFieldName: string) => {
    setSectionInputFields((sFs) =>
      sFs.map((sF) => ({
        ...sF,
        inputProps: sF.inputProps.map((iP) => {
          const newIP = cloneDeep(iP);
          if (newIP.sideFields?.some((sideField) => sideField.name === sideFieldName)) {
            newIP.inputSettings = {
              ...newIP.inputSettings,
              isPhoneNumber: type === ContactNumberType.CELL || type === ContactNumberType.PHONE,
            };
          }
          return newIP;
        }),
      })),
    );
  };

  // MAIN CONTENT
  const formContents = useMemo(() => {
    const initValues: Record<string, any> = {};
    const componentsArray: any[][] = [];
    const priorities: Record<string, number> = {};
    // isInputError is for checking if any input error exists to assign for the inputErrorRef
    let isInputError = false;
    // create initValues and componentsArray, priorities by Sections
    sectionInputFields.forEach((sectionInput, sectionIndex: number) => {
      const isContact = !!sectionInput?.isContact;
      const allowSectionAddMore = sectionInput.allowAddMore;
      // when there is no records, create an "Add" button to start adding record
      if (sectionInput.inputProps.length === 0) {
        // use Date.now() as a unique key for creating new input with unique name
        if (sectionInput.required) {
          isInputError = true;
        }
        const date = Date.now();
        componentsArray[sectionIndex] = [
          <div key={sectionIndex}>
            {sectionInput.required && (
              <ErrorRequiredStyled className='input-error'>Should have at least 1 record</ErrorRequiredStyled>
            )}
            <Button
              // eslint-disable-next-line react/no-array-index-key
              type='dashed'
              className='addable-addMore-button'
              onClick={() => {
                const inputFieldTemplate = sectionInput.allowAddMoreTemplate;
                const { schemaTemplate, complexSchemaConverter } = sectionInput;
                setSectionInputFields((c) => {
                  const current = [...c];
                  if (inputFieldTemplate) {
                    const newSideFieldInputNamesWithKeys = inputFieldTemplate.sideFields?.map((sF) => ({
                      ...sF,
                      name: `${sF.name}-${date}`,
                    }));
                    current[sectionIndex].inputProps = [
                      {
                        ...inputFieldTemplate,
                        name: `${inputFieldTemplate.name}-${date}`,
                        sideFields: newSideFieldInputNamesWithKeys,
                      },
                    ];
                  }
                  return current;
                });
                if (schemaTemplate) {
                  setValidationState((c) => {
                    const current = { ...c };
                    if (inputFieldTemplate) {
                      const newFieldInputName = inputFieldTemplate.name;
                      current[`${newFieldInputName}-${date}`] = schemaTemplate?.[newFieldInputName];
                      inputFieldTemplate.sideFields?.forEach((sF) => {
                        current[`${sF.name}-${date}`] = schemaTemplate?.[sF.name];
                      });
                    }
                    if (complexSchemaConverter) {
                      return complexSchemaConverter(current, date.toString());
                    }
                    return current;
                  });
                }
              }}>
              + Add
            </Button>
          </div>,
        ];
      }

      // when there is some record, render input fields accordingly
      sectionInput.inputProps.forEach((fieldInput, fieldIndex: number) => {
        const inputName = fieldInput.name;
        const { sideFields, disabled: isInputDisabled } = fieldInput;
        const defaultValueInit = fieldInput.defaultValue || get(userData, inputName);
        const defaultValue = !isObject(defaultValueInit) ? defaultValueInit : '';
        const labelType = get(fieldInput, 'labelType', LABEL_TYPE.above);
        initValues[inputName] = defaultValue || '';
        priorities[inputName] = fieldIndex;
        let clearRowButton: ReactElement | null = null;

        // Subfield, or inputs that are on the same row
        const defaultSideFieldSize = 16 / get(sideFields, 'length', 1);
        let totalSideFieldSize = 0;
        const manySideFieldNames: string[] = [];
        const SideFieldComponents =
          sideFields &&
          sideFields.map((sideField, sideFieldIndex) => {
            const sideFieldSize =
              get(sectionInput, ['sizes', sideFieldIndex]) || sideField.size || defaultSideFieldSize;
            const sideFieldName = sideField.name;
            const sideFieldDefaultValue = sideField.defaultValue || get(userData, sideFieldName, '');
            totalSideFieldSize += sideFieldSize;
            initValues[sideFieldName] = sideFieldDefaultValue;
            manySideFieldNames.push(sideFieldName);
            priorities[sideFieldName] = fieldIndex;
            return (
              <SideFieldContainerColStyled key={sideFieldName} span={24} lg={sideFieldSize}>
                <InputComponentStyled
                  {...sideField}
                  {...(isContact && isContactTypeField(sideField as any)
                    ? { onChange: (type: ContactNumberType) => handleContactTypeChange(type, sideField.name) }
                    : {})}
                  type={get(sideField, 'type', INPUT_TYPE.TEXT)}
                  defaultValue={sideFieldDefaultValue}
                  labelType={sideField.labelType || labelType}
                  label={!sectionInput.labelProps ? sideField.label : undefined}
                />
              </SideFieldContainerColStyled>
            );
          });

        // Add more and clear record buttons
        if (allowSectionAddMore) {
          const onClearClick = getClearButtonOnClickFunction(
            fieldIndex,
            sectionIndex,
            setSectionInputFields,
            setValidationState,
            sectionInput,
          );

          clearRowButton = !isInputDisabled ? (
            <Row align='middle' className='gx-my-0' style={{ flexWrap: 'nowrap' }}>
              <ClearButtonIconContainerStyled align='middle' onClick={onClearClick}>
                <span className='icon icon-trash' />
              </ClearButtonIconContainerStyled>
            </Row>
          ) : null;
        }

        // push to component list
        if (!componentsArray[sectionIndex]) componentsArray[sectionIndex] = [];
        componentsArray[sectionIndex].push(
          <UpdateProfileInput
            // disabled prop is for SortableElement
            // dragDisable prop is for UpdateProfileInput component
            dragDisable={!fieldInput.draggable}
            disabled={!fieldInput.draggable}
            sideFieldComponents={SideFieldComponents}
            fieldInput={fieldInput}
            totalSideFieldSize={totalSideFieldSize}
            defaultValue={defaultValue}
            extra={clearRowButton}
            index={fieldIndex}
            draggable={sectionInput.draggable}
            key={inputName}
            sectionData={sectionInput}
          />,
        );
      });
    });

    const onSortEnd = (newIndex: number, oldIndex: number, sectionIndex: number) => {
      setSectionInputFields((c) => {
        const current = [...c];
        if (get(current, [sectionIndex, 'inputProps', newIndex, 'draggable'])) {
          current[sectionIndex].inputProps = arrayMove(current[sectionIndex].inputProps, oldIndex, newIndex);
        }
        return current;
      });
    };

    // Input field component: containing sections, which contain side fields, main fields, labels and buttons
    const InputFieldRow = componentsArray.map((comp, sectionIndex: number) => {
      const sectionData = sectionInputFields[sectionIndex];
      const addMoreButtonOnClickFunction = getAddMoreButtonOnClickFunction(
        sectionIndex,
        setSectionInputFields,
        setValidationState,
        sectionData,
      );

      return (
        <div key={`section-${sectionIndex}`}>
          {(sectionData.sectionTitle || (sectionData.labelProps && sectionData.labelProps.length > 0)) && (
            <SectionHeaderContainerStyled>
              {sectionData.sectionTitle && (
                <SectionTitleContainerStyled className={`section-title section-title-index-${sectionIndex}`} span={24}>
                  {sectionData.sectionTitle}
                </SectionTitleContainerStyled>
              )}
              {sectionData.inputProps.length > 0 && (
                <>
                  {sectionData.draggable && <Col span={1} />}
                  {sectionData.labelProps && (
                    <Col flex='auto'>
                      <LabelsContainerStyled>
                        {sectionData.labelProps.map((lb, lbIndex: number) => (
                          <Col key={`section-input-${lbIndex}`} span={sectionData.sizes?.[lbIndex] || 24}>
                            {lb}
                          </Col>
                        ))}
                      </LabelsContainerStyled>
                    </Col>
                  )}
                </>
              )}
            </SectionHeaderContainerStyled>
          )}
          <UpdateProfileInputsContainer
            onSortEnd={(v: SortEnd) => {
              onSortEnd(v.newIndex, v.oldIndex, sectionIndex);
            }}
            useDragHandle>
            {comp}
          </UpdateProfileInputsContainer>
          {sectionData.inputProps.length > 0 && sectionData.allowAddMore && (
            <AddMoreButtonStyled type='dashed' className='addable-addPlus-more' onClick={addMoreButtonOnClickFunction}>
              <span style={{ fontSize: '16px' }}>+&nbsp;</span>
              <span>
                <IntlMessages id='userProfile.addRow' />
              </span>
            </AddMoreButtonStyled>
          )}
        </div>
      );
    });

    inputErrorRef.current = isInputError;

    return [initValues, InputFieldRow, priorities];
  }, [sectionInputFields, validationState, userData, width]);

  const [initValues, InputFieldRow, priorities] = useMemo(() => formContents, [sectionInputFields, userData]);

  const [defaultSubmitLoading, updateUserFunction] = useUpdateUser(
    undefined,
    showNotificationOnSuccess,
    showNotificationOnFailure,
  );

  const onFormSubmit = async (value: any) => {
    if (inputErrorRef.current) return;
    const finalSubmitValues = Object.keys(value).reduce((acc, inputName) => {
      if (!inputName.includes('-')) {
        acc[inputName] = value[inputName];
        return acc;
      }
      const baseName = getBaseInputName(inputName);
      if (!acc[baseName]) acc[baseName] = [];
      acc[baseName][priorities[inputName]] = value[inputName];
      return acc;
    }, {} as Record<string, any[]>);

    if (onSubmit) {
      return onSubmit(finalSubmitValues);
    }
    const response = await updateUserFunction(finalSubmitValues);
    onSubmitCallback?.(finalSubmitValues);
    return response;
  };

  const yupValidation = validationState ? Yup.object(validationState) : undefined;
  const containerClass =
    portalButtonClassName ||
    (pathname.split('/')[1] === 'setup-wizard'
      ? '.wizard-save-button'
      : '.custom-tabs-content-active .gx-tab-content-header');

  const innerFormContainerRef = useRef<any>();
  return (
    <div className={className}>
      <Formik
        initialValues={initValues}
        onSubmit={onFormSubmit}
        validationSchema={yupValidation}
        innerRef={formRef || ref}
        validateOnBlur>
        {(props) => (
          <Form className='gx-signin-form gx-form-row0' ref={innerFormContainerRef}>
            {InputFieldRow}
            {inputComponentsFromOutside?.()}
            <Row justify='end' className={hideSaveButton ? 'gx-d-none' : ''}>
              <Col span={24}>
                <Row justify='end'>
                  <PortalProfileSaveButton containerClass={containerClass} disabled={displaySaveOnFooter}>
                    <FormActionButtonStyled
                      isLoading={loading || defaultSubmitLoading}
                      buttonType='primary'
                      className={`gx-mb-0 gx-mr-0 ${hideSaveButton ? 'gx-d-none' : ''} profile-save-button`}
                      onClick={() => {
                        const firstErrorElement = innerFormContainerRef.current?.querySelector('.input-error');
                        if ((!isEmpty(props.errors) || inputErrorRef) && firstErrorElement) {
                          firstErrorElement.scrollIntoView(false);
                        }
                        props.handleSubmit();
                      }}>
                      {submitButtonLabel || <IntlMessages id='userProfile.saveProfile' />}
                    </FormActionButtonStyled>
                  </PortalProfileSaveButton>
                </Row>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default React.memo(WithAddableInputFormTemplate);
